// src/components/calendar/CalendarGrid.jsx
import { useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import parseStringDate from "../../utils/parseStringDate";
import { getEventTypeStyle as canonicalStyle } from "../../utils/eventTypeStyles";

// ==========================================
// SORT HELPERS
// ==========================================

/**
 * Sort a day's events by their manual sortOrder (small numbers = top), then
 * by start time as a tiebreaker for events that never had a manual order
 * assigned. Events with `sortOrder === undefined` fall through to a very
 * large value so they end up at the bottom.
 */
const sortByManualOrder = (a, b) => {
  const ao = typeof a.sortOrder === "number" ? a.sortOrder : Number.MAX_SAFE_INTEGER;
  const bo = typeof b.sortOrder === "number" ? b.sortOrder : Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return parseStringDate(a.start) - parseStringDate(b.start);
};

/**
 * DayEventItem
 *
 * One reorderable event row inside the "+X more" day-events popup. The drag
 * handle is a dedicated grip icon — clicking the rest of the row still opens
 * the editor — so users don't accidentally start a drag when they meant to
 * click Edit.
 */
const DayEventItem = ({ event, style, onEdit }) => {
  const controls = useDragControls();
  const startTime = parseStringDate(event.start).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endTime = parseStringDate(event.end).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Reorder.Item
      value={event}
      dragListener={false}
      dragControls={controls}
      className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors duration-200 cursor-default"
    >
      <div className="flex items-start gap-3">
        {/* Drag handle — only this triggers a reorder drag */}
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 p-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 4a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm6-15a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3 ${style.bg} ${style.text}`}
            >
              <span className="mr-1">{style.icon}</span>
              {event.eventType}
            </span>
            <span className="text-sm text-gray-500">
              {startTime} - {endTime}
            </span>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-1 truncate">
            {event.title}
          </h4>
          {event.location && (
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <svg
                className="w-4 h-4 mr-1 flex-shrink-0 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="truncate">{event.location}</span>
            </div>
          )}
          {event.description && (
            <p className="text-sm text-gray-600 mb-3">{event.description}</p>
          )}
        </div>

        <button
          onClick={onEdit}
          className="ml-2 shrink-0 px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors duration-200"
        >
          Edit
        </button>
      </div>
    </Reorder.Item>
  );
};

/**
 * CalendarGrid Component
 *
 * Renders a monthly calendar grid with events displayed as colored chips.
 * Handles month navigation and event interactions.
 */
const CalendarGrid = ({
  events,
  currentDate,
  onDateChange,
  onEditEvent,
  onReorderDay,
}) => {
  // State for showing all events in a day
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Hold the most recent reorder so we can persist on drag end. Using a ref
  // (not state) avoids triggering a re-render mid-drag inside Reorder.Group.
  const latestOrderRef = useRef(null);
  // Was the order actually changed? If the user just opened+closed the
  // popup without dragging, skip the Firestore writeback.
  const hasReorderedRef = useRef(false);
  // ==========================================
  // CALENDAR UTILITIES
  // ==========================================

  /**
   * Get the first day of the month
   */
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  /**
   * Generate calendar grid days (6 weeks = 42 days)
   */
  const getCalendarDays = () => {
    const firstDay = getFirstDayOfMonth(currentDate);
    const startDate = new Date(firstDay);

    // Start from Sunday of the week containing the first day
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const days = [];
    const currentDay = new Date(startDate);

    // Generate 42 days for consistent grid
    for (let i = 0; i < 42; i++) {
      days.push({
        date: new Date(currentDay),
        isCurrentMonth: currentDay.getMonth() === currentDate.getMonth(),
        isToday: currentDay.toDateString() === new Date().toDateString(),
      });
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  /**
   * Get events for a specific date
   */
  const getEventsForDate = (date) => {
    const dateStr = date.toDateString();
    return events.filter((event) => {
      const eventDate = parseStringDate(event.start).toDateString();
      return eventDate === dateStr;
    });
  };

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================

  const goToPreviousMonth = () => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handle showing all events for a specific day.
   *
   * Sort the day's events by their manual sortOrder so the popup opens in the
   * order the user last set, not by start time. Without this, our optimistic
   * reorder would survive only until the parent re-renders with the
   * default-sorted events list.
   */
  const handleShowAllEvents = (date, dayEvents) => {
    const ordered = [...dayEvents].sort(sortByManualOrder);
    setSelectedDate(date);
    setSelectedDayEvents(ordered);
    latestOrderRef.current = ordered;
    hasReorderedRef.current = false;
    setShowAllEventsModal(true);
  };

  /**
   * Update local state during the drag. framer-motion calls this on each
   * pointer move. We don't persist here — that would spam Firestore — but we
   * record that a reorder happened so the close handler can flush it.
   */
  const handleLocalReorder = (newOrder) => {
    setSelectedDayEvents(newOrder);
    latestOrderRef.current = newOrder;
    hasReorderedRef.current = true;
  };

  /**
   * Persist the latest order to Firestore via the parent's onReorderDay.
   * No-op when nothing was reordered or the parent didn't supply the
   * callback. Called from every modal close path so the user never loses a
   * drag they thought "took".
   */
  const handlePersistReorder = () => {
    if (
      hasReorderedRef.current &&
      onReorderDay &&
      latestOrderRef.current
    ) {
      onReorderDay(latestOrderRef.current);
    }
  };

  /**
   * Close the all events modal
   */
  const handleCloseAllEventsModal = () => {
    handlePersistReorder();
    setShowAllEventsModal(false);
    setSelectedDayEvents([]);
    setSelectedDate(null);
    latestOrderRef.current = null;
    hasReorderedRef.current = false;
  };

  // ==========================================
  // STYLING UTILITIES
  // ==========================================

  /**
   * Get event styling based on type — delegates to the canonical
   * palette in `src/utils/eventTypeStyles.js`. CalendarGrid uses the
   * SOLID 500-level background with white text (compact pills on the
   * month/week grid). See utility for the full set of class variants.
   */
  const getEventTypeStyle = (type) => {
    const s = canonicalStyle(type);
    return { bg: s.bgSolid, text: "text-white", icon: s.icon };
  };

  // ==========================================
  // RENDER
  // ==========================================

  const days = getCalendarDays();
  const monthYear = currentDate.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Previous Month Button */}
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-200 rounded-md transition-colors duration-200"
              title="Previous month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Month/Year Display */}
            <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
              {monthYear}
            </h2>

            {/* Next Month Button */}
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-200 rounded-md transition-colors duration-200"
              title="Next month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Today Button */}
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors duration-200"
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="p-2 text-center text-sm font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days Grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day.date);

            return (
              <div
                key={index}
                className={`min-h-[100px] p-1 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                  !day.isCurrentMonth ? "bg-gray-100 text-gray-400" : ""
                } ${day.isToday ? "bg-blue-50 border-blue-300" : ""}`}
                onClick={() => onEditEvent(null, day.date)}
                title={`Click to add event for ${day.date.toDateString()}`}
              >
                {/* Day Number */}
                <div
                  className={`text-sm font-medium mb-1 ${
                    day.isToday
                      ? "text-blue-600 font-bold"
                      : day.isCurrentMonth
                      ? "text-gray-900"
                      : "text-gray-400"
                  }`}
                >
                  {day.date.getDate()}
                </div>

                {/* Events for this day */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event) => {
                    const style = getEventTypeStyle(event.eventType);
                    return (
                      <div
                        key={event.id}
                        className={`text-xs p-1 rounded ${style.bg} ${style.text} truncate cursor-pointer hover:opacity-80 transition-opacity duration-200`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditEvent(event);
                        }}
                        title={`${event.title} - Click to edit`}
                      >
                        <span className="inline-block mr-1">{style.icon}</span>
                        {event.title}
                      </div>
                    );
                  })}

                  {/* Show "+X more" if there are more than 3 events */}
                  {dayEvents.length > 3 && (
                    <div 
                      className="text-xs text-gray-500 px-1 py-0.5 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowAllEvents(day.date, dayEvents);
                      }}
                      title="Click to see all events for this day"
                    >
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Events Modal */}
      {showAllEventsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={handleCloseAllEventsModal}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl sm:max-w-3xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] max-h-[90vh] flex flex-col resize overflow-auto transform transition-all duration-300">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Events for {selectedDate?.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <button
                  onClick={handleCloseAllEventsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body — flex-1 fills the space between sticky header
                  and footer, scrolls when content overflows.
                  Each row carries a drag handle (Reorder.Item) so the user
                  can manually order events for this day. */}
              <div className="p-6 flex-1 overflow-y-auto">
                {selectedDayEvents.length > 0 ? (
                  <Reorder.Group
                    axis="y"
                    values={selectedDayEvents}
                    onReorder={handleLocalReorder}
                    className="space-y-3"
                  >
                    {selectedDayEvents.map((event) => (
                      <DayEventItem
                        key={event.id}
                        event={event}
                        style={getEventTypeStyle(event.eventType)}
                        onEdit={() => {
                          // close handler flushes any pending reorder
                          handleCloseAllEventsModal();
                          onEditEvent(event);
                        }}
                      />
                    ))}
                  </Reorder.Group>
                ) : (
                  <p className="text-gray-500 text-center py-8">No events found for this day.</p>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    handleCloseAllEventsModal();
                    onEditEvent(null, selectedDate);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  Add New Event
                </button>
                <button
                  onClick={handleCloseAllEventsModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Close
                </button>
              </div>

              {/* Visible resize cue — the browser's native handle for CSS
                  `resize` is hard to see on a white background with rounded
                  corners. This SVG makes the drag target obvious.
                  pointer-events-none so the underlying native handle still
                  receives the drag gesture. */}
              <div
                className="absolute bottom-1 right-1 pointer-events-none text-gray-400"
                aria-hidden="true"
              >
                <svg width="14" height="14" viewBox="0 0 14 14">
                  <path
                    d="M 2 13 L 13 2 M 5 13 L 13 5 M 8 13 L 13 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarGrid;
